import { useEffect, useState } from "react";
import { CSSObject } from "@emotion/react";
import { IonSpinner } from "@ionic/react";
import { loadVGSCollect } from "@vgs/collect-js";
import {
  IVGSCollectForm,
  useVGSCollectFormInstance,
  VGSCollectFormState,
  VGSCollectSession,
} from "@vgs/collect-js-react";
import { Button, CheckboxInput } from "swing-components";

import { ErrorMessage, Toast, useAuth } from "~components";
import type { PIIAnswerType } from "~onboarding/utils";
import {
  api,
  GET,
  translate as t,
  VGS_COLLECT_VERSION,
  VGS_CUSTOM_DOMAIN,
  VGS_ENVIRONMENT,
  VGS_VAULT_ID,
} from "~utils";

import "./PIIAnswer.css";

const { SSNField, DateField, TextField } = VGSCollectSession;

const VALID = "valid";

// These custom field styles are injected into a VGS field. Since
// each VGS field is in an iframe, all values must be hardcoded.
const styles: CSSObject = {
  "@font-face": {
    fontFamily: "Poppins",
    fontStyle: "normal",
    fontWeight: 400,
    fontDisplay: "swap",
    src: "url(https://fonts.gstatic.com/s/poppins/v24/pxiEyp8kv8JHgFVrJJfecnFHGPc.woff2) format('woff2')",
    unicodeRange:
      "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
  },
  fontFamily: "Poppins, sans-serif",
  fontSize: "16px",
  fontWeight: 400,
  color: "#4f5b6d", // --swing-slate600
  "&::placeholder": {
    color: "#bec5d0", // --swing-slate300
  },
};

type PIIAnswerProps = Omit<PIIAnswerType, "type"> & {
  value?: string;
  onChange: (value?: string) => void;
  isReadOnly?: boolean;
};

/**
 * For this component to work, ensure all VGS_* environment variables are set. Refer
 * to those pulled in from ~utils above.
 *
 * Also, ensure the 'Inbound Route' configuration in VGS is configured to redact
 * the PII fields being submitted by this component - as of now, that's only 'ssn'
 * and 'dob'.
 *
 * For development, use 'ngrok http http://localhost:8000' to expose the local
 * API server. Then configure the 'Inbound Route' configuration in VGS to use
 * the ngrok URL.
 *
 * Note, each VGS field is wrapped in an iframe, so none of the user input values
 * are accessible to the parent component.
 */
export function PIIAnswer(props: PIIAnswerProps) {
  const { onChange, value, isReadOnly } = props;
  const { authInfo } = useAuth();
  const [isScriptLoaded, setScriptLoaded] = useState(false);
  const [isFormSubmitting, setFormSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(value === VALID);
  const [showToast, setShowToast] = useState(false);
  const [form] = useVGSCollectFormInstance() as [IVGSCollectForm | null];
  const [isSubmitError, setIsSubmitError] = useState(false);
  const TODAY = new Date();
  const isFieldDisabled = isReadOnly || value === VALID;

  const onFormSubmit = async (event: any) => {
    event.preventDefault();

    if (isFormSubmitting || !form) return;

    setFormSubmitting(true);

    try {
      // Call an authenticated endpoint so the Axios interceptor can refresh an
      // expired token before submitting the form to VGS Collect.
      await GET("/api/sub/me");

      const authorizationHeader = api.defaults.headers.common.Authorization;
      const authorization =
        typeof authorizationHeader === "string" && authorizationHeader.length > 0
          ? authorizationHeader
          : `Token ${authInfo.token}`;

      form.submit(
        // The VGS 'Inbound Route' configuration must be configured to use this endpoint.
        "api/sub/pii",
        {
          withCredentials: true,
          headers: {
            Authorization: authorization,
          },
        },
        (status) => {
          if (status === 200) {
            onChange(VALID);
            setShowToast(true);
          }
          setFormSubmitting(false);
          form?.reset();
        },
        (errors) => {
          // eslint-disable-next-line no-console
          console.error("Error: VGS form.submit", errors);
          setFormSubmitting(false);
          setIsSubmitError(true);
          form?.reset();
        },
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error: onFormSubmit", error);
      setFormSubmitting(false);
      setIsSubmitError(true);
      form?.reset();
    }
  };

  const onFormUpdate = (state: VGSCollectFormState) => {
    setIsFormValid(
      (state?.["legal-first-name"]?.isValid ?? false) &&
        (state?.["legal-last-name"]?.isValid ?? false) &&
        (state?.["dob"]?.isValid ?? false) &&
        (state?.["ssn"]?.isValid ?? false),
    );
  };

  const onFormError = (errors: VGSCollectFormState) => {
    // eslint-disable-next-line no-console
    console.error("Error: VGS form", errors);
  };

  useEffect(() => {
    loadVGSCollect({
      vaultId: VGS_VAULT_ID,
      environment: VGS_ENVIRONMENT,
      version: VGS_COLLECT_VERSION,
    }).then(() => setScriptLoaded(true));
  }, []);

  if (!isScriptLoaded) {
    return <IonSpinner name="dots" />;
  }

  return (
    <div css={{ marginTop: "var(--spacing-2xl)" }}>
      <VGSCollectSession
        vaultId={VGS_VAULT_ID}
        environment={VGS_ENVIRONMENT}
        cname={VGS_CUSTOM_DOMAIN}
        formId="pii-form"
        onCustomSubmit={onFormSubmit}
        stateCallback={onFormUpdate}
        onErrorCallback={onFormError}
      >
        <Block>
          <Block styles={{ gap: "var(--spacing-5xl)" }}>
            <Field>
              <Label>{t("LEGAL_FIRST_NAME")}</Label>
              <TextField
                name="legal-first-name"
                placeholder={t("LEGAL_FIRST_NAME_PLACEHOLDER")}
                validations={["required"]}
                style={styles}
                {...(isFieldDisabled ? { disabled: true } : {})}
              />
            </Field>
            <Field>
              <Label>{t("LEGAL_LAST_NAME")}</Label>
              <TextField
                name="legal-last-name"
                placeholder={t("LEGAL_LAST_NAME_PLACEHOLDER")}
                validations={["required"]}
                style={styles}
                {...(isFieldDisabled ? { disabled: true } : {})}
              />
            </Field>
            <Field>
              <Label>{t("DOB")}</Label>
              <DateField
                name="dob"
                validations={
                  [
                    "required",
                    {
                      type: "compareDate",
                      params: { field: TODAY, function: "less" },
                    },
                  ] as string[]
                }
                min={"1900-01-01" as unknown as number}
                max={TODAY.toISOString().slice(0, 10) as unknown as number}
                style={{ ...styles, "&.invalid": { color: "#bec5d0" } }}
                {...(isFieldDisabled ? { disabled: true } : {})}
              />
            </Field>
            <Field>
              <Label>{t("SSN")}</Label>
              <SSNField
                name="ssn"
                validations={["required", "validSSN"]}
                placeholder={t("SSN_PLACEHOLDER")}
                style={styles}
                {...(isFieldDisabled ? { disabled: true } : {})}
              />
            </Field>
            <Field>
              <Label>{t("CONFIRM_PII_LABEL")}</Label>
              <div>{t("CONFIRM_PII_DESCRIPTION")}</div>
              <CheckboxInput
                label={t("CONFIRM_PII_YES_LABEL")}
                isChecked={isConfirmed}
                isDisabled={!isFormValid || isFieldDisabled}
                onChange={(isChecked) => setIsConfirmed(isChecked)}
              />
            </Field>
          </Block>
          <Button
            type="primary"
            status={
              isFormSubmitting ? "loading" : isFormValid && isConfirmed ? "enabled" : "disabled"
            }
            isFluid
            onClick={() => {}}
          >
            {t("LABEL_SUBMIT_INFO")}
          </Button>
          {isSubmitError && (
            <ErrorMessage>
              <span>
                {t("PII_SUBMIT_ERROR_MESSAGE")} {t("LABEL_CONTACT_SUPPORT_1")}{" "}
                <a target="_blank" rel="noreferrer" href="mailto:support@swingeducation.com">
                  {t("LABEL_CONTACT_SUPPORT")}
                </a>
                .
              </span>
            </ErrorMessage>
          )}
        </Block>
      </VGSCollectSession>
      <Toast
        isVisible={showToast}
        type="positive"
        onDismiss={() => setShowToast(false)}
        timeoutMs={2000}
        text={t("PII_SUBMIT_SUCCESS_TOAST_MSG")}
      />
    </div>
  );
}

function Block(props: { styles?: CSSObject; children: React.ReactNode }) {
  return (
    <div
      css={{
        gap: "var(--spacing-lg)",
        display: "flex",
        flexDirection: "column",
        ...props.styles,
      }}
    >
      {props.children}
    </div>
  );
}

function Label(props: { children: React.ReactNode }) {
  return (
    <div
      css={{
        fontSize: "var(--swing-font-md)",
        fontWeight: "var(--swing-font-semibold)",
      }}
    >
      {props.children}
    </div>
  );
}

function Field(props: { children: React.ReactNode }) {
  return <Block styles={{ gap: "var(--spacing-md)" }}>{props.children}</Block>;
}
